import {useState} from 'react';
import {useRouter} from 'next/router';
import {Flex, Text, IconButton, Box, Button, useToast, RadioGroup} from '@chakra-ui/react';

import {useTranslate} from '@eksab/i18n';
import {AddIcon} from '@eksab/assets/icons';

import {PaymentCard, useMyCardsQuery} from '@eksab/features/profile/hooks/useMyCardsQuery';
import type {PackageType} from '@eksab/features/store/types';

import {PaymentCardItem} from './';
import {useBuyPackageMutation} from '../hooks/useBuyPackageMutation';

type CardPaymentProps = {
  packageId: number;
  code: string;
  type: PackageType;
};

export function CardPayment({packageId, code, type}: CardPaymentProps) {
  const toast = useToast();
  const {push} = useRouter();
  const [selectedCard, setSelectedCard] = useState<PaymentCard>();

  const t = useTranslate();
  const myCardsQuery = useMyCardsQuery();
  const buyPackageMutation = useBuyPackageMutation<'CARD' | 'TOKEN'>(
    {
      package_id: packageId,
      package_type: type,
      discount_voucher_code: code || undefined,
    },
    {
      onSuccess: (res) => res.next.action !== 'add-coins' && push(res.next.url),
      onError: (e) => toast({status: 'error', title: e.response?.data?.message ?? t('app.errors.try-again')}),
    },
  );

  const onCardChange = (token: string) => setSelectedCard(myCardsQuery?.data?.find((card) => card.token === token));

  const onPay = (method: 'card' | 'token' = 'card') => {
    fbq?.('track', 'InitiateCheckout');
    mixpanel?.track('Button Clicked', {button: 'payment.cc.pay_now'});

    buyPackageMutation.mutate(
      method === 'card'
        ? {payment_method: 'CARD'}
        : {
            payment_method: 'TOKEN',
            token: selectedCard!.token,
          },
    );
  };

  return (
    <>
      <Flex flex={1} minH={['unset', null, 24]}>
        {myCardsQuery.data?.length ? (
          <Box p={{base: 5, lg: 7}} pt="5" shadow="lg" flex="1" rounded="md">
            <Flex align="center" justify="space-between" mb="3.5">
              <Text fontWeight="800">{t('payment.method.cc.select')}</Text>

              <IconButton
                minW="unset"
                boxSize="7"
                variant="unstyled"
                aria-label={t('payment.method.cc.add')}
                title={t('payment.method.cc.add')}
                icon={<AddIcon width="full" />}
                onClick={() => onPay()}
              >
                {t('payment.method.cc.pay-with-new-card')}
              </IconButton>
            </Flex>

            <RadioGroup display="flex" flexDir="column" gap="2" onChange={onCardChange} value={selectedCard?.token}>
              {myCardsQuery.data.map((card) => (
                <PaymentCardItem key={card.id} value={card.token} card={card} />
              ))}
            </RadioGroup>

            <Button mt="4" w="full" isDisabled={!selectedCard} onClick={() => onPay('token')}>
              {t('app.actions.pay_now')}
            </Button>
          </Box>
        ) : (
          <Flex flex="1" mt="10" flexDir="column" align="center" justify="center" textAlign="center">
            {myCardsQuery.isLoading ? (
              <Text>{t('app.state.loading')}</Text>
            ) : myCardsQuery.isError ? (
              <Text>{t('app.errors.try-again')}</Text>
            ) : (
              <>
                <Text mb="5">{t('profile.wallet.no-cards')}</Text>

                <Button onClick={() => onPay()}> {t('payment.method.cc.pay-with-new-card')}</Button>
              </>
            )}
          </Flex>
        )}
      </Flex>
    </>
  );
}
